import React, { useState } from "react";
import { Box, Stack, Link, useTheme } from "@mui/material";
import { Card, Pill, DSTypography, Badge } from "../../design-system";
import { AudioWaveform } from "../../common/AudioWaveform";

export type Experience = {
  title: string;
  company: string;
  startDate: string;
  endDate: string;
  description: string;
  skillSet: string[];
  companyUrl?: string;
  highlights?: string[];
  isCurrent?: boolean;
};

const HIGHLIGHTS_PREVIEW = 3;

const renderHighlightWithLinks = (text: string) => {
  // Map of client names to their URLs
  const clientLinks: Record<string, string> = {
    GoodNature: "https://www.goodnature.com/",
    FeelGoodz: "https://feelgoodz.com/",
  };

  // Project/system names to make bold (ordered by length - longer first to avoid partial matches)
  const projectNames = [
    "Early Intervention System",
    "Training System",
    "Report PDF Review",
    "Personnel System",
    "Accounting Module",
    "Shopify integration",
    "ERP modules",
    "EIS",
  ];

  // Client names (only public/commercial clients, not agency names)
  const clientNames = ["GoodNature", "FeelGoodz"];

  // Build regex pattern for all project names (escape special chars)
  const projectPattern = projectNames
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");

  // Build regex pattern for client/agency names
  const clientNamePattern = clientNames
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");

  // Combined pattern: client links OR project names OR client/agency names
  const combinedPattern = new RegExp(
    `(${projectPattern}|${clientNamePattern}|GoodNature|FeelGoodz)`,
    "gi"
  );

  const parts = text.split(combinedPattern);
  const result: React.ReactNode[] = [];

  parts.forEach((part, idx) => {
    if (!part) return; // Skip empty strings

    const lowerPart = part.toLowerCase();

    // Check if it's a client link
    if (clientLinks[part]) {
      result.push(
        <Link
          key={`link-${idx}`}
          href={clientLinks[part]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {part}
        </Link>
      );
      return;
    }

    // Check if it's a project name (case-insensitive)
    const isProjectName = projectNames.some((name) => name.toLowerCase() === lowerPart);

    if (isProjectName) {
      result.push(
        <Box key={`project-${idx}`} component="span" sx={{ fontWeight: 600 }}>
          {part}
        </Box>
      );
      return;
    }

    // Check if it's a client/agency name (case-insensitive)
    const isClientName = clientNames.some((name) => name.toLowerCase() === lowerPart);

    if (isClientName) {
      result.push(
        <Box key={`client-${idx}`} component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
          {part}
        </Box>
      );
      return;
    }

    // Regular text
    result.push(<span key={`text-${idx}`}>{part}</span>);
  });

  return <>{result}</>;
};

export const ExperienceCard = ({ experience }: { experience: Experience }) => {
  const theme = useTheme();
  const {
    title,
    company,
    startDate,
    endDate,
    description,
    skillSet,
    companyUrl,
    highlights,
    isCurrent,
  } = experience;
  const expStyles = theme.custom.componentStyles.experienceCard;
  const [expanded, setExpanded] = useState(false);

  const visibleHighlights = highlights
    ? expanded
      ? highlights
      : highlights.slice(0, HIGHLIGHTS_PREVIEW)
    : [];
  const hasMore = highlights ? highlights.length > HIGHLIGHTS_PREVIEW : false;

  return (
    <Card variant="gold">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Stack
          direction="row"
          spacing={theme.spacing(expStyles.headerSpacing)}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: theme.spacing(expStyles.headerMarginBottom) }}
        >
          <DSTypography variant="dateRange">
            {startDate} – {endDate}
          </DSTypography>
          {isCurrent && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Badge label="Now Playing" variant="teal" />
              <Box sx={{ width: 40 }}>
                <AudioWaveform height={16} color="primary.main" bars={6} />
              </Box>
            </Stack>
          )}
        </Stack>

        <DSTypography variant="experienceTitle">{title}</DSTypography>

        {companyUrl ? (
          <Link
            href={companyUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mb: theme.spacing(expStyles.companyMarginBottom) }}
          >
            <DSTypography variant="companyName">{company}</DSTypography>
          </Link>
        ) : (
          <DSTypography
            variant="companyName"
            sx={{ mb: theme.spacing(expStyles.companyMarginBottom) }}
          >
            {company}
          </DSTypography>
        )}

        {/* Description */}
        <DSTypography
          variant="description"
          sx={{
            mb: highlights?.length
              ? theme.spacing(expStyles.descriptionMarginBottom)
              : theme.spacing(expStyles.descriptionMarginBottomNoHighlights),
          }}
        >
          {description}
        </DSTypography>

        {/* Highlights (impact bullets) */}
        {highlights?.length ? (
          <Stack
            spacing={theme.spacing(expStyles.highlightsSpacing)}
            sx={{ mb: theme.spacing(expStyles.highlightsMarginBottom) }}
          >
            {visibleHighlights.map((item, idx) => (
              <Box key={idx} sx={{ position: "relative" }}>
                <DSTypography
                  variant="caption"
                  sx={{
                    color: "text.primary",
                    lineHeight: 1.6,
                    position: "relative",
                    pl: theme.spacing(expStyles.highlightMarker.padding),
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "0.5em",
                      transform: "translateY(-50%)",
                      width: "3px",
                      height: "3px",
                      borderRadius: theme.custom.borderRadius.full,
                      bgcolor: "primary.main",
                      opacity: expStyles.highlightMarker.opacity,
                    },
                  }}
                >
                  {renderHighlightWithLinks(item)}
                </DSTypography>
              </Box>
            ))}
            {hasMore && (
              <DSTypography
                variant="caption"
                onClick={() => setExpanded((prev) => !prev)}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                {expanded ? "Show less" : `+${highlights!.length - HIGHLIGHTS_PREVIEW} more`}
              </DSTypography>
            )}
          </Stack>
        ) : null}

        {/* Skills */}
        <Stack
          direction="row"
          spacing={theme.spacing(expStyles.skillsSpacing)}
          flexWrap="wrap"
          sx={{ mt: theme.spacing(expStyles.skillsMarginTop) }}
        >
          {skillSet.map((skill, idx) => (
            <Pill key={skill + idx.toString()} variant="skill">
              {skill}
            </Pill>
          ))}
        </Stack>
      </Box>
    </Card>
  );
};
